import React, { useEffect, useState } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";
import { useParams } from "react-router-dom";
import TestimonialAPI from "../../api/TestimonialApi";
import { Rating } from "react-simple-star-rating";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import ImageUploader from "../../components/partials/ImageUploader";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import SubmitButton from "../../components/partials/global/SubmitButton";

function Edit() {
  useAlphabeticInput();

  const params = useParams();
  const id = params.id || {};

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    customer_name: "",
    designation: "",
    company_name: "",
    message: "",
    rating: 0,
    image: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleRating = (rate) => {
    setFormData((prev) => ({
      ...prev,
      rating: rate,
    }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  useEffect(() => {
    const fetchSingleDetails = async () => {
      try {
        const res = await TestimonialAPI.getSingleDetails(id);

        if (res.code === 200) {
          const result = res.data;

          setFormData({
            customer_name: result.customer_name,
            designation: result.designation,
            company_name: result.company_name,
            message: result.message,
            rating: result.rating,
            image: result.image,
          });

          setOriginalData({
            customer_name: result.customer_name || "",
            designation: result.designation || "",
            company_name: result.company_name || "",
            message: result.message || "",
            rating: result.rating || "",
            image: result.image || "",
          });
        } else {
          showError(res.message);
        }
      } catch (err) {
        showError(err.message);
      }
    };

    fetchSingleDetails();
  }, [id]);

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_name) {
      newErrors.customer_name = "Name Field is required";
    }

    if (!formData.designation) {
      newErrors.designation = "Designation Field is required";
    }

    if (!formData.message) {
      newErrors.message = "Message Field is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getChangedFields = (original, current) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      if (current[key] !== original[key]) {
        if (key === "image" && !current[key]) return;
        changed[key] = current[key];
      }
    });

    return changed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    showLoader();

    try {
      const payload = getChangedFields(originalData, formData);

      if (Object.keys(payload).length === 0) {
        showError("No changes detected");
        return;
      }

      const res = await TestimonialAPI.updateDetails(id, payload);

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Edit Testimonial</h4>
            <h6>
              Update testimonial content, customer details, or images as needed.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Update Details</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="User Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="testimonials"
              />

              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Customer Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control only-alphabets"
                        placeholder="Customer Name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.customer_name && (
                      <span className="errors text-danger">
                        {errors.customer_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Designation <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-pencil"></i>
                      </span>
                      <input
                        type="text"
                        name="designation"
                        className="form-control only-alphabets"
                        placeholder="Designation"
                        value={formData.designation}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.designation && (
                      <span className="errors text-danger">
                        {errors.designation}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Company Name</label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-building"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Company Name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyName && (
                      <span className="errors text-danger">
                        {errors.companyName}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Rating</label>
                    <div>
                      <Rating
                        onClick={handleRating}
                        initialValue={formData.rating}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <label className="form-label">
                    Message <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" id="basic-addon1">
                      <i className="fa fa-comment"></i>
                    </span>
                    <textarea
                      value={formData.message}
                      className="form-control"
                      name="message"
                      onChange={handleChange}
                    ></textarea>
                  </div>
                  {errors.message && (
                    <span className="errors text-danger">{errors.message}</span>
                  )}
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Submit Details"
                    loadingText="Submitting..."
                  ></SubmitButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default Edit;
